import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

const QuizPlay = ({
  question,
  questionNumber,
  totalQuestions,
  selectedAnswer,
  correctAnswerIndex,
  hasAnswered,
  lastResultIsCorrect,
  progress,
  timeLeft,
  loading,
  error,
  onAnswer,
  onNext,
  onSubmit,
  onBackToMenu,
  showFeedback,
  feedback,
  answerExplanation,
  isFetchingNext,
  timedOut,
}) => {
  const [musicMuted, setMusicMuted] = useState(
    () => localStorage.getItem("quiz_music_muted") === "true"
  );
  const [sfxMuted, setSfxMuted] = useState(
    () => localStorage.getItem("quiz_sfx_muted") === "true"
  );
  const timerAudioRef = useRef(null);
  const correctAudioRef = useRef(null);
  const wrongAudioRef = useRef(null);

  useEffect(() => {
    timerAudioRef.current = new Audio("/sfx/quiz_timer.mp3");
    timerAudioRef.current.loop = true;
    timerAudioRef.current.volume = 0.35;
    correctAudioRef.current = new Audio("/sfx/quiz_correct.mp3");
    correctAudioRef.current.volume = 0.6;
    wrongAudioRef.current = new Audio("/sfx/quiz_wrong.mp3");
    wrongAudioRef.current.volume = 0.6;

    return () => {
      timerAudioRef.current?.pause();
      correctAudioRef.current?.pause();
      wrongAudioRef.current?.pause();
    };
  }, []);

  useEffect(() => {
    localStorage.setItem("quiz_music_muted", String(musicMuted));
  }, [musicMuted]);

  useEffect(() => {
    localStorage.setItem("quiz_sfx_muted", String(sfxMuted));
  }, [sfxMuted]);

  useEffect(() => {
    const timerAudio = timerAudioRef.current;
    if (!timerAudio) return;
    if (!question || hasAnswered || musicMuted) {
      timerAudio.pause();
      timerAudio.currentTime = 0;
      return;
    }
    timerAudio.play().catch(() => {});
    return () => {
      timerAudio.pause();
      timerAudio.currentTime = 0;
    };
  }, [hasAnswered, musicMuted, question]);

  useEffect(() => {
    if (!showFeedback || sfxMuted) return;
    if (timedOut || lastResultIsCorrect === false) {
      wrongAudioRef.current?.play().catch(() => {});
      return;
    }
    if (lastResultIsCorrect === true) {
      correctAudioRef.current?.play().catch(() => {});
    }
  }, [lastResultIsCorrect, sfxMuted, showFeedback, timedOut]);
  if (!question) {
    return <EmptyState>Esta trivia no tiene preguntas aun.</EmptyState>;
  }

  return (
    <>
      <QuizTop>
        <span>Pregunta {questionNumber}</span>
      </QuizTop>
      <SoundRow>
        <SoundButton type="button" onClick={() => setMusicMuted((prev) => !prev)}>
          {musicMuted ? "Música: Off" : "Música: On"}
        </SoundButton>
        <SoundButton type="button" onClick={() => setSfxMuted((prev) => !prev)}>
          {sfxMuted ? "FX: Off" : "FX: On"}
        </SoundButton>
      </SoundRow>
      <TimerRow>
        <span>Tiempo: {Math.max(timeLeft, 0)}s</span>
        <TimerTrack>
          <TimerBar $width={(Math.max(timeLeft, 0) / 30) * 100} />
        </TimerTrack>
      </TimerRow>
      {/* <ProgressTrack>
        <ProgressBar $width={progress} />
      </ProgressTrack> */}
      <QuestionCard>
        <h3>{question.question}</h3>
      </QuestionCard>
      <OptionsList>
        {question.options.map((option, index) => {
          const isSelected = selectedAnswer === index;
          const isCorrect = hasAnswered && index === correctAnswerIndex;
          const showCorrect = hasAnswered && isCorrect;
          const showIncorrect = hasAnswered && isSelected && !isCorrect;
          const state = showCorrect
            ? "correct"
            : showIncorrect
              ? "incorrect"
              : isSelected
                ? "selected"
                : hasAnswered
                  ? "disabled"
                  : "default";

          return (
            <OptionButton
              key={option}
              type="button"
              onClick={() => onAnswer(index)}
              disabled={hasAnswered || loading}
              $state={state}
            >
              <span>{option}</span>
              {showCorrect && <OptionBadge $state="correct">✓</OptionBadge>}
              {showIncorrect && <OptionBadge $state="incorrect">✕</OptionBadge>}
            </OptionButton>
          );
        })}
      </OptionsList>
      {error && <StatusText $error>{error}</StatusText>}
      {showFeedback && feedback && <FeedbackText $timeout={timedOut}>{feedback}</FeedbackText>}
      {showFeedback && answerExplanation && (timedOut || lastResultIsCorrect === false) && (
        <ExplanationCard>
          <strong>Explicacion</strong>
          <p>{answerExplanation}</p>
        </ExplanationCard>
      )}
      {/* Boton oculto temporalmente: la respuesta se envia automaticamente al terminar el tiempo.
      {selectedAnswer !== null && !showFeedback && (
        <PrimaryButton type="button" onClick={onSubmit} disabled={loading}>
          Responder
        </PrimaryButton>
      )} */}
      {showFeedback && (
        <FeedbackActions>
          <PrimaryButton
            type="button"
            onClick={onNext}
            disabled={loading || isFetchingNext}
            $loading={isFetchingNext}
          >
            {isFetchingNext && <Spinner aria-hidden="true" />}
            {isFetchingNext ? "Cargando siguiente..." : "Siguiente"}
          </PrimaryButton>
          <SecondaryButton type="button" onClick={onBackToMenu} disabled={loading || isFetchingNext}>
            Volver al menu
          </SecondaryButton>
        </FeedbackActions>
      )}
    </>
  );
};

export default QuizPlay;

const EmptyState = styled.div`
  padding: 30px 0;
  text-align: center;
  color: #6b7280;
`;

const QuizTop = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 12px;
`;

const SoundRow = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-bottom: 8px;
`;

const SoundButton = styled.button`
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #0f172a;
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 11px;
  cursor: pointer;
`;

const TimerRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 12px;
  color: #0f172a;
  margin-bottom: 10px;
`;

const TimerTrack = styled.div`
  flex: 1;
  height: 6px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
`;

const TimerBar = styled.div`
  height: 100%;
  background: #ff0102;
  border-radius: 999px;
  transition: width 0.3s linear;
  width: ${({ $width }) => `${$width || 0}%`};
`;

const QuestionCard = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: 24px;
  margin-bottom: 16px;
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.06);

  h3 {
    margin: 0;
    font-size: 20px;
    text-align: center;
    color: #0f172a;
  }
`;

const OptionsList = styled.div`
  display: grid;
  gap: 12px;
  margin-bottom: 18px;
`;

const StatusText = styled.p`
  margin: 6px 0 0;
  font-size: 13px;
  text-align: center;
  color: ${({ $error }) => ($error ? "#b91c1c" : "#6b7280")};
`;

const ExplanationCard = styled.div`
  margin-top: 12px;
  padding: 14px 16px;
  border-radius: 14px;
  background: #fff7ed;
  border: 1px solid #fdba74;
  color: #9a3412;

  strong {
    display: block;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 6px;
  }

  p {
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
  }
`;

const PrimaryButton = styled.button`
  border: none;
  border-radius: 999px;
  padding: 12px 18px;
  background: #ff0102;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  margin-top: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  padding: 12px 16px;
  background: #ffffff;
  color: #0f172a;
  font-weight: 600;
  cursor: pointer;
  width: 100%;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const FeedbackActions = styled.div`
  display: grid;
  gap: 10px;
`;

const FeedbackText = styled.p`
  margin: 10px 0 0;
  text-align: center;
  font-size: 14px;
  color: ${({ $timeout }) => ($timeout ? "#b91c1c" : "#0f172a")};
`;

const Spinner = styled.span`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #ffffff;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const OptionButton = styled.button`
  border-radius: 16px;
  padding: 16px 18px;
  text-align: left;
  border: 2px solid transparent;
  background: #ffffff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  transition: all 0.2s ease;

  ${({ $state }) => {
    if ($state === "correct") {
      return "border-color: #16a34a; background: #ecfdf5; color: #166534;";
    }
    if ($state === "incorrect") {
      return "border-color: #ef4444; background: #fef2f2; color: #991b1b;";
    }
    if ($state === "disabled") {
      return "border-color: #e5e7eb; background: #f8fafc; color: #94a3b8;";
    }
    if ($state === "selected") {
      return "border-color: #ff0102; background: #fff1f2; color: #991b1b;";
    }
    return "border-color: #e5e7eb; color: #0f172a;";
  }}

  &:hover {
    border-color: #ff0102;
  }
`;

const OptionBadge = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  color: ${({ $state }) => ($state === "correct" ? "#16a34a" : "#ef4444")};
  background: ${({ $state }) => ($state === "correct" ? "#dcfce7" : "#fee2e2")};
`;
